import * as PIXI from "pixi.js";
import {
  Spine,
  MixBlend,
  MixDirection,
} from "@esotericsoftware/spine-pixi-v7";
import anime from "animejs";
import { PixiSettings } from "../../../../../../pixiSettings";
import {
  ASPECT_RATIO_BREAKPOINTS,
  ULTRA_WIDE,
} from "../../../../../../Contexts/DimensionsContext";
import { FONT_STYLES, scaleStyle } from "../../../../../../fontStyles";
import { FEATURES_CONFIG } from "../../../../../../featuresConfig";
import {
  publishTapWordAnchor,
  tapWordNudge,
} from "../../../../../../utils/tapWordAnchor";
import { getNewUITexture } from "../../../../../../assets";
import { soundManager } from "../../../../../../SoundManager";
import { SOUND_NAME } from "../../../../../../sounds";
import TickerTimeout from "../../../../../../utils/TickerTimeout";

// ─── Max win multiplier ───────────────────────────────────────────────────────
const MAX_WIN_MULTIPLIER =
  FEATURES_CONFIG.POT_COLLECTION?.MAX_WIN_MULTIPLIER ||
  FEATURES_CONFIG.HOLD_AND_WIN?.MAX_WIN_MULTIPLIER ||
  10000;

// ─── Slider constants (mirrors PaytableSlider) ─────────────────────────────────
const NAV_THROTTLE_MS = 100;
const SWIPE_THRESHOLD = 50;
const VELOCITY_THRESHOLD = 0.3;
const AUTOPLAY_INTERVAL_MS = 3000;
// Set false to disable automatic slide advancing (swipe + dots keep working).
const SLIDER_AUTOPLAY_ENABLED = true;
// Below this pointer travel, a press+release counts as a tap (closes the popup),
// not a swipe. Distinguishes tap-to-start from a deliberate drag.
const TAP_MOVE_THRESHOLD = 10;
// Pagination dots use real spritesheet circles (not Graphics) — PIXI Graphics
// circles render with jagged / aliased edges that a border-radius fix can't solve.
const DOT_TEX_NAME_ACTIVE = "button_buy_bonus_background"; // yellow circle
const DOT_TEX_NAME_INACTIVE = "volume_bar_handle"; // white circle

// ============================================================================
// INTRO POPUP LAYOUT CONFIGURATION
// Fine-tune background scale and padding per device and orientation.
// bgScaleMult  : how large the background oval fills the content area (1.0 = exact fit)
// padMult      : top/bottom protected zone = uiSymbolSize × padMult
// sidePadMult  : left/right protected zone = uiSymbolSize × sidePadMult
// ============================================================================

const LAYOUT_MOBILE_SMALL_PORTRAIT = {
  bgScaleMult: 1.35,
  padMult: 0.5,
  sidePadMult: 0.1,
};

const LAYOUT_MOBILE_SMALL_LANDSCAPE = {
  bgScaleMult: 1.0,
  padMult: 0.75,
  sidePadMult: 0.5,
};

const LAYOUT_MOBILE_PORTRAIT = {
  bgScaleMult: 1.35,
  padMult: 0.5,
  sidePadMult: 0.1,
};

const LAYOUT_MOBILE_LANDSCAPE = {
  bgScaleMult: 1.0,
  padMult: 0.75,
  sidePadMult: 0.5,
};

const LAYOUT_TABLET_PORTRAIT = {
  bgScaleMult: 1.05,
  padMult: 1.0,
  sidePadMult: 0.2,
};

const LAYOUT_TABLET_LANDSCAPE = {
  bgScaleMult: 0.9,
  padMult: 1.0,
  sidePadMult: 0.5,
};

const LAYOUT_SMALL_DESKTOP = {
  bgScaleMult: 1.2,
  padMult: 1.0,
  sidePadMult: 0.5,
};

const LAYOUT_DESKTOP = {
  bgScaleMult: 1.25,
  padMult: 1.0,
  sidePadMult: 0.5,
};

const LAYOUT_WIDE_DESKTOP = {
  bgScaleMult: 1.0,
  padMult: 1.0,
  sidePadMult: 0.5,
};

const LAYOUT_ULTRA_WIDE_DESKTOP = {
  bgScaleMult: 0.975,
  padMult: 1.0,
  sidePadMult: 0.5,
};

const LAYOUT_CONFIGS = {
  MOBILE_SMALL_PORTRAIT: LAYOUT_MOBILE_SMALL_PORTRAIT,
  MOBILE_SMALL_LANDSCAPE: LAYOUT_MOBILE_SMALL_LANDSCAPE,
  MOBILE_PORTRAIT: LAYOUT_MOBILE_PORTRAIT,
  MOBILE_LANDSCAPE: LAYOUT_MOBILE_LANDSCAPE,
  TABLET_PORTRAIT: LAYOUT_TABLET_PORTRAIT,
  TABLET_LANDSCAPE: LAYOUT_TABLET_LANDSCAPE,
  SMALL_DESKTOP: LAYOUT_SMALL_DESKTOP,
  DESKTOP: LAYOUT_DESKTOP,
  WIDE_DESKTOP: LAYOUT_WIDE_DESKTOP,
  ULTRA_WIDE_DESKTOP: LAYOUT_ULTRA_WIDE_DESKTOP,
};

const getLayoutConfig = (device, isPortrait, isMobileSmall, screenWidth) => {
  const orientation = isPortrait ? "PORTRAIT" : "LANDSCAPE";
  if (isMobileSmall) return LAYOUT_CONFIGS[`MOBILE_SMALL_${orientation}`];
  if (device === PixiSettings.DEVICE.MOBILE)
    return LAYOUT_CONFIGS[`MOBILE_${orientation}`];
  if (device === PixiSettings.DEVICE.TABLET)
    return LAYOUT_CONFIGS[`TABLET_${orientation}`];
  if (screenWidth && screenWidth >= ULTRA_WIDE.BREAKPOINT)
    return LAYOUT_CONFIGS.ULTRA_WIDE_DESKTOP;
  if (
    screenWidth &&
    screenWidth >= ASPECT_RATIO_BREAKPOINTS.WIDE_DESKTOP_MIN_WIDTH
  )
    return LAYOUT_CONFIGS.WIDE_DESKTOP;
  if (
    screenWidth &&
    screenWidth <= ASPECT_RATIO_BREAKPOINTS.SMALL_DESKTOP_MAX_WIDTH
  )
    return LAYOUT_CONFIGS.SMALL_DESKTOP;
  return LAYOUT_CONFIGS.DESKTOP;
};
const SLIDE_CONTENT_SCALE = 0.7;

// ---------------------------------------------------------------------------
// PORTRAIT / LANDSCAPE element ratios — slider content between max-win & tap
// Fixed slot positions (ratios of slide area) keep every slide aligned identically.
// ---------------------------------------------------------------------------
const PORTRAIT_CFG = {
  logoWRatio: 0.171,
  logoYRatio: -0.935,
  // Glow behind the logo — sized off the LOGO, not the background, so the two
  // always read as one object. The Y offset is a fraction of the logo height,
  // nudging the glow down so it reads as centred on the artwork rather than
  // on the logo's bounding box.
  glowScaleVsLogo: 1.6,
  glowYOffsetVsLogo: 0.08,
  maxWinFontRatio: 0.0325,
  maxWinYRatio: -0.6575,
  slideTopGapRatio: 0.03,
  dotsGapRatio: 0.13,
  // Dots' own Y, as a fraction of the background height BELOW the background
  // centre — the same convention as tapYRatio. Set this and the dots stop
  // hanging off the tap text (dotsGapRatio) and are placed directly. Delete it
  // to go back to the gap behaviour.
  dotsYRatio: 0.625,
  dotSizeRatio: 0.02,
  dotGapRatio: 0.016,
  tapFontRatio: 0.035,
  tapGapRatio: 0.04,
  tapYRatio: 1.035,
  // Single-image slides in portrait: the image is sized against the
  // BACKGROUND height, not the slide area, so this reads as "how much of the
  // background the image fills". The text is then hung off the image's real
  // bottom edge by slideSingleTextGapRatio, so the two can never overlap
  // however big the image gets.
  slideSingleHeightVsBgRatio: 0.7,
  slideSingleTextGapRatio: -0.025,
  // Slide description font, as a multiplier on the tap-text size it otherwise
  // inherits. Lets the slide text shrink without touching TAP TO START.
  slideDescFontMult: 0.85,
  // Share of the slide width the description may occupy. Below the old fixed
  // 0.88, which let the long Mad Labs lines run to both screen edges — see
  // _fitDescFontSize, which shrinks the size until the widest line fits this.
  slideDescWrapRatio: 0.78,
  // Slide area bottom — independent of dots (bgCY + bgH × ratio).
  // This is also the MASK's bottom edge: a taller image needs it pushed down
  // or the image is cut off. It must still stay above dotsYRatio.
  slideAreaBottomRatio: 0.55,
  slideTopCenterRatio: 0.14,
  slideBottomCenterRatio: 0.35,
  slideImageOffsetYRatio: 0.085,
  slideDescTopRatio: 0.78,
  slideTopMaxWRatio: 0.5,
  slideBottomMaxWRatio: 0.56,
  slideTopMaxHRatio: 0.2,
  slideBottomMaxHRatio: 0.18,
  slideSingleCenterRatio: 0.36,
  slideSingleMaxWRatio: 0.6,
  slideSingleMaxHRatio: 0.44,
};

const LANDSCAPE_CFG = {
  logoWRatio: 0.1824,
  logoYRatio: -0.52,
  // See PORTRAIT_CFG — kept per-orientation so it can be tuned separately.
  glowScaleVsLogo: 1.6,
  glowYOffsetVsLogo: 0.08,
  maxWinFontRatio: 0.036,
  maxWinYRatio: -0.329,
  slideTopGapRatio: 0,
  dotsGapRatio: 0.055,
  dotSizeRatio: 0.014,
  dotGapRatio: 0.01,
  tapFontRatio: 0.0225,
  tapGapRatio: 0.13,
  tapYOffsetRatio: 0.145,
  contentCenterYRatio: 0.08,
  cardWRatio: 0.29,
  slideAreaBottomPadRatio: 0.012,
  slidePairedRowCenterRatio: 0.34,
  slidePairedRowMaxHRatio: 0.52,
  slidePairedLeftMaxWRatio: 0.36,
  slidePairedRightMaxWRatio: 0.36,
  slidePairedGapRatio: 0.03,
  slideImageOffsetYRatio: 0,
  slideDescTopRatio: 0.75,
  slideSingleCenterRatio: 0.38,
  slideSingleMaxWRatio: 0.44,
  slideSingleMaxHRatio: 0.4,
  // Single-image slides run HORIZONTALLY in landscape: image on the left,
  // text on the right, both on one row. Portrait keeps the vertical stack —
  // it has the height for it and not the width. Sizes are untouched; only the
  // positions differ, so the same image and font size are used either way.
  // Setting slideSingleImageXRatio is what switches the layout on.
  slideSingleRowCenterRatio: 0.38,
  slideSingleImageXRatio: 0.3,
  slideSingleTextXRatio: 0.72,
  slideSingleTextWrapRatio: 0.42,
};

// Slide definitions — top + optional bottom image, description below.
// Shared assets for portrait and landscape.
// Every slide is a SINGLE image plus text — no split pot/coins pairs. A null
// bottomTex is what selects that layout, which in landscape means image left,
// text right (see LANDSCAPE_CFG.slideSingleImageXRatio). All four share the
// super-turbo slide's multipliers because the four images are near-identical
// in size: 864x598, 832x582, 754x536 and 791x551.
//
// THE ORDER IS THE GAME RULES' ORDER (September 2026): free spins, Golden
// Reveal, mystery reveals, super turbo. Each line was matched to the picture
// it sits under, not to the slide number — the three startpage images were
// still carrying Zeus's collect/mystery/double copy, which described neither
// this game nor these pictures.
//
// The line breaks are authored, and wordWrap stays ON underneath them: the
// break is where the copy wants it, and a narrow screen may still fold a long
// line again rather than running it off the card.
const SLIDE_DEFS = [
  {
    // A reel with the Wild among potions, then the same reel filled by the Wild.
    topTex: "startpage_image_1",
    bottomTex: null,
    descKey: "start_page_expanding_wild_title",
    descDefault: "EXPANDING WILDS ON REELS 2, 3 AND 4\nCOVER THE WHOLE REEL",
    portraitSizeMult: 1.15,
    landscapeSizeMult: 2.75,
    landscapeCenterOffsetRatio: 0,
  },
  {
    // The Wild with its x2 medallion.
    topTex: "startpage_image_2",
    bottomTex: null,
    descKey: "start_page_wild_multiplier_title",
    descDefault: "A WILD ON REEL 3\nDOUBLES EVERY WIN THROUGH IT",
    portraitSizeMult: 1.15,
    landscapeSizeMult: 2.75,
    landscapeCenterOffsetRatio: 0,
  },
  {
    // The Sun and Moon Scatters.
    topTex: "startpage_image_3",
    bottomTex: null,
    descKey: "start_page_scatters_title",
    descDefault: "SUN AND MOON SCATTERS\nPAY IN ANY POSITION",
    portraitSizeMult: 1.15,
    landscapeSizeMult: 2.75,
    landscapeCenterOffsetRatio: 0,
  },
  {
    topTex: "intro_feature_superturbo",
    bottomTex: null,
    descKey: "start_page_turbo_title",
    descDefault: "FASTER GAMEPLAY\nWITH SUPER TURBO SPINS",
    portraitSizeMult: 1.15,
    landscapeSizeMult: 2.75,
    landscapeCenterOffsetRatio: 0,
  },
];

/**
 * The slide description's style, without a size.
 *
 * A function rather than a constant because it is read twice for different
 * reasons — once to MEASURE the copy and once to DRAW it — and those two must
 * be the same style or the fit below is computed against a face that never
 * reaches the screen. The long comment on the stroke is at the draw site.
 */
const descStyleBase = () => ({
  ...FONT_STYLES.gambleFreeSpins,
  fill: "#ffffff",
  dropShadow: false,
  stroke: "#000000",
  strokeThickness: 7,
  lineJoin: "round",
});

const TOTAL_SLIDES = SLIDE_DEFS.length;

/**
 * The feature card's native proportions — see _computeLandscapeContentBottomY.
 *
 * These are the REAL dimensions of the texture that call used to read
 * (intro_feature_mystery_pot, 572x399), not the 542x746 fallback that sat
 * beside it. The two never matched, and because the texture was always loaded
 * the fallback never ran — so 572x399 is what has always shipped. Using the
 * fallback's numbers instead changed the aspect from 1.43 to 0.73 and broke the
 * layout.
 */
const CARD_ASPECT_W = 572;
const CARD_ASPECT_H = 399;

// ─── Loading spine (occupies the tap-to-start slot while the game loads) ──────
// The same preloader spine the loader plays, already warm in the PIXI cache.
// The skeleton holds the animated POPIPLAY wordmark and nothing else.
// Set false to render no spine while loading — the tap slot simply sits empty
// until the game is ready. The rest of the sequence is unaffected.
const BG_ALIAS = "intro_background_landscape";

/**
 * The intro background, or null while it is still on its way.
 *
 * Cache-guarded because getLoadingSpineTargetHeight is called by the LOADER,
 * on its very first layout, before any of the intro bundle has landed.
 * PIXI.Assets.get warns for an alias that is registered but not yet loaded, so
 * a bare get logged on every one of those early frames.
 *
 * This is the same guard LoaderController._createArtwork puts on its own three
 * images, which is why none of those ever warned.
 */
const getBackgroundTexture = () =>
  PIXI.Assets.cache.has(BG_ALIAS) ? PIXI.Assets.get(BG_ALIAS) : null;

/**
 * The native size of `intro_background_landscape.webp`, for the window in which
 * the texture is not in the cache yet.
 *
 * ── This has to MATCH the file, and it did not ──
 *
 * Everything on both the loading screen and the intro is measured off this
 * background — the logo, the tap text and the popiplay wordmark are all ratios
 * of its rendered width. So the fallback is not a rough stand-in: it decides the
 * layout outright whenever the texture is missing.
 *
 * It used to say 812 x 406, which is neither this file's size nor its shape
 * (2.000 against the real 1.804). On a COLD load the loader runs before the
 * intro's background is in the PIXI cache, took the fallback, and sized the
 * wordmark against a background that does not exist. On the next reload the
 * texture was cached, the real numbers were used, and it looked right — which
 * is exactly the "first reload broke it, second fixed it" Vladimir reported.
 *
 * ONE pair for both orientations, because this game ships one background and
 * portrait reads the landscape image. The old code carried a separate portrait
 * pair (388 x 689) that nothing could ever produce.
 *
 * If the file is replaced, re-measure it. A guess here is a broken first load.
 */
const BG_NATIVE_W = 1508;
const BG_NATIVE_H = 836;

const LOADING_SPINE_ENABLED = true;
const LOADING_SPINE_SKELETON_ALIAS = "preloader-skeleton";
const LOADING_SPINE_ATLAS_ALIAS = "preloader-atlas";

/**
 * How many frames the wordmark may wait for a usable layout before it is shown
 * regardless. 120 is two seconds at 60Hz — far longer than the one or two
 * frames it actually takes, and short enough that a rig which can never be
 * measured does not hold a rAF for the life of the screen.
 */
const LOADING_SPINE_LAYOUT_MAX_FRAMES = 120;
// The wordmark's bone lives at y = -364.43, nowhere near the skeleton root, so
// positioning the spine by its origin drops the letters far below the target.
// Pose the animation, measure what's actually left, and centre THAT instead.
// Sampled mid-animation because every letter fades/attaches on its own
// schedule (last one at 0.7667s) — measuring at t=0 would catch a partial word.
const LOADING_SPINE_POSE_TIME = 1.2;
// Wordmark height as a multiple of the tap text's font size, so it sits in the
// same visual slot as the text it replaces.
const LOADING_SPINE_HEIGHT_MULT = 1.6;
// Safety cap on waiting for the wordmark's loop to finish. The animation runs
// ~2.9s, so this only trips if the complete event never arrives.
const LOADING_SPINE_COMPLETE_MAX_WAIT_MS = 4000;

// ─── Wordmark hand-over ──────────────────────────────────────────────────────
// The loader and this popup draw the SAME looping wordmark, and the loader
// cross-fades into this popup while both are on screen. Building a fresh spine
// here at time zero made the logo visibly restart in the middle of that fade —
// finished and frozen on the loader, rebuilding itself letter by letter on the
// popup — and then charged the player a SECOND full revolution (~2.9s) before
// tap-to-start was allowed to appear.
//
// So the loader publishes how far its own track had run, and the spine built
// here picks the animation up from exactly that point. One continuous
// revolution across both screens instead of two chopped ones.
// Stamped with the time it was published: this popup's spine is built a frame
// or two after the loader hands over, and the loader's own copy keeps running
// in the meantime, so the gap is added back on at creation.
let handover = null;

export const setLoadingSpineHandoverTime = (trackTime) => {
  handover =
    Number.isFinite(trackTime) && trackTime > 0
      ? { trackTime, at: performance.now() }
      : null;
};

const takeHandoverTrackTime = () => {
  if (!handover) return 0;
  const elapsedSeconds = Math.max(0, performance.now() - handover.at) / 1000;
  const trackTime = handover.trackTime + elapsedSeconds;
  handover = null; // one hand-over per boot; a later spine starts clean
  return trackTime;
};

/**
 * Posed bounds of the wordmark, measured WITHOUT disturbing playback.
 *
 * The letters sit far from the skeleton root, so the spine has to be posed
 * before it can be measured. Both screens used to do that with
 * `state.update(POSE_TIME)`, which advances the LIVE track: the wordmark then
 * began its visible pass 1.2s in, already half-built — the "unfinished" look
 * on screen — and a second call could push the track past the end of the loop
 * and fire a spurious `complete`, resolving the hand-off early.
 *
 * Applying the animation straight to the skeleton leaves AnimationState
 * untouched: no track advance, no events. The live pose is restored right
 * after.
 *
 * Returns null when nothing measurable was posed, so the caller retries on the
 * next layout instead of caching an empty box forever — a cached empty box
 * left the wordmark at scale 1 on the skeleton origin, which is roughly 364px
 * off target, i.e. invisible.
 *
 * @returns {{x: number, y: number, width: number, height: number}|null}
 */
export const measureLoadingSpineBounds = (spine, poseTime) => {
  if (!spine?.skeleton) return null;

  const previousScaleX = spine.scale.x;
  const previousScaleY = spine.scale.y;
  spine.scale.set(1);

  try {
    const animation = spine.skeleton.data?.animations?.[0];
    if (animation) {
      spine.skeleton.setToSetupPose();
      animation.apply(
        spine.skeleton,
        0,
        poseTime,
        false,
        [],
        1,
        MixBlend.setup,
        MixDirection.mixIn,
      );
      spine.skeleton.updateWorldTransform();
      // getLocalBounds measures the slot MESHES, not the skeleton, and those
      // are only rebuilt on render — posing alone would have measured whatever
      // frame was last drawn. hasNeverUpdated is cleared first because PIXI's
      // own bounds pass runs a live state update when it is still set, which
      // would throw away the pose above and measure a half-built wordmark.
      spine.hasNeverUpdated = false;
      spine.renderMeshes?.();
    }
  } catch {
    // Non-fatal — measure whatever pose it is in.
  }

  const measured = spine.getLocalBounds();
  const bounds =
    measured?.width > 0 && measured?.height > 0
      ? {
          x: measured.x || 0,
          y: measured.y || 0,
          width: measured.width,
          height: measured.height,
        }
      : null;

  // Put the live pose back — the measurement left the skeleton on a frame the
  // animation is not actually on.
  try {
    spine.state?.apply(spine.skeleton);
    spine.skeleton.updateWorldTransform();
    spine.renderMeshes?.();
  } catch {
    // Silent fail — the next ticker update restores it anyway.
  }

  spine.scale.set(previousScaleX, previousScaleY);
  return bounds;
};

/**
 * Target on-screen height of the PopiPlay wordmark, in pixels.
 *
 * THE single source of truth for that size. The loader shows the same spine
 * and must render it at exactly the same size on every device and
 * orientation, so it imports this rather than keeping its own copy of the
 * maths — two copies would drift the moment either side is tuned.
 *
 * The size is derived the same way the tap text it stands in for is derived:
 * from the intro background's fitted width. Only the POSITION differs between
 * the two screens.
 *
 * @param {object} dimensionsContext - needs dimensions, device, orientation,
 *   uiSymbolSize and isMobileSmall. Note uiSymbolSize is the symbol WIDTH.
 * @returns {number} height in pixels, or 0 when it cannot be computed
 */
export const getLoadingSpineTargetHeight = (dimensionsContext) => {
  const { dimensions, device, orientation, uiSymbolSize, isMobileSmall } =
    dimensionsContext || {};
  if (!dimensions?.width || !dimensions?.height || !uiSymbolSize) return 0;

  const isPortrait = orientation === PixiSettings.ORIENTATION.PORTRAIT;
  const { width, height } = dimensions;

  const layoutCfg = getLayoutConfig(device, isPortrait, isMobileSmall, width);
  const contentH = height - 2 * (uiSymbolSize * layoutCfg.padMult);
  const contentW = width - 2 * (uiSymbolSize * layoutCfg.sidePadMult);

  // Same texture and same fallbacks as _updateLayout. Portrait reads the
  // landscape image because this game ships one background for both.
  const bgTex = getBackgroundTexture();
  const bgNativeW = bgTex?.width || BG_NATIVE_W;
  const bgNativeH = bgTex?.height || BG_NATIVE_H;
  const bgW =
    bgNativeW *
    Math.min(contentW / bgNativeW, contentH / bgNativeH) *
    layoutCfg.bgScaleMult;

  const cfg = isPortrait ? PORTRAIT_CFG : LANDSCAPE_CFG;
  return Math.round(bgW * cfg.tapFontRatio) * LOADING_SPINE_HEIGHT_MULT;
};

/**
 * IntroPopupComposedController — PIXI display objects for the composed intro
 * popup: static chrome (bg, logo, max win, tap-to-start) plus a swipeable
 * feature-image slider with pagination dots and swipe navigation.
 */
class IntroPopupComposedController {
  constructor(container, dimensionsContext, t) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.t = t;

    // Loading spine — stands in for the tap text until the game finishes loading
    this._loadingSpine = null;
    // null (not false) so the first setGameLoaded call always applies, even
    // when the game is already loaded by the time this popup is built.
    this._isGameLoaded = null;
    this._tapRevealAnims = null;
    // True once the tap word has finished arriving — see _revealTapText and
    // isTapReady. Nothing may close the popup before it.
    this._tapRevealComplete = false;
    this._spineCompleteTimeout = null;
    this._loadingSpineBounds = null;
    // rAF handle for the "wait for a real layout before showing it" retry.
    this._loadingSpineLayoutRAF = null;

    // Static display objects
    this._blackBg = null;
    this._background = null;
    this._logoGlow = null;
    this._logo = null;
    this._maxWinText = null;
    this._tapContainer = null;
    this._tapText = null;

    // Slider display objects
    this._sliderGroup = null;
    this._slidesMaskContainer = null;
    this._slidesMaskGfx = null;
    this._slidesTrack = null;
    this._realSlides = [];
    this._dotsContainer = null;
    this._dotEntries = [];

    // Textures (cached at init — assets already loaded)
    this._bgLandscapeTex = getBackgroundTexture();
    // One background for both orientations. This game ships a single intro
    // image, so portrait points at the same texture rather than its own.
    // Everything downstream still reads the two fields, so the per-orientation
    // layout is untouched and can take a second image again at any time.
    this._bgPortraitTex = this._bgLandscapeTex;
    this._logoTex = PIXI.Assets.get("logo");
    // Force-loaded by the secondary LoaderController, which always runs to
    // completion before this popup is built — so by now it is warm in the
    // cache. Missing simply means no glow; the logo still renders.
    // this._logoGlowTex = PIXI.Assets.get("logo_glow");
    // Mad Labs' glow image is gone (static cleanup, 2026-09-30) — asked only
    // if it is there, so the cache does not warn on every load.
    this._logoGlowTex = PIXI.Assets.cache.has("logo_glow")
      ? PIXI.Assets.get("logo_glow")
      : null;

    // Click handler (set by component via setClickHandler)
    this._clickHandler = null;
    // Fullscreen handler (set via setFullscreenHandler) — triggered on swipe so
    // touch navigation enters fullscreen just like a tap does, without closing.
    this._fullscreenHandler = null;

    // Hover / press animation refs (for cleanup)
    this._hoverAnim = null;
    this._pressAnim = null;
    this._isPressAnimating = false;

    // Intro entrance animation refs
    this._introAnims = null;

    // Slider state
    this._currentSlide = 0;
    this._slideWidth = 0;
    this._slideHeight = 0;
    this._trackPos = { x: 0 };
    this._trackAnim = null;
    this._isTransitioning = false;
    this._isJumping = false;
    this._isPaginationNav = false;
    this._lastNavTime = 0;
    this._fadeEasing = anime.easing("easeInOutCubic");

    // Autoplay state
    this._autoplayTickerCallback = null;
    this._autoplayElapsed = 0;

    // Drag state
    this._isDragging = false;
    this._dragStartX = 0;
    this._dragStartTrackX = 0;
    this._dragCurrentX = 0;
    this._dragStartTime = 0;

    // Layout cache
    this._dotsCenterY = 0;
    this._tapTextY = 0;
    this._layoutBgW = 0;
    this._layoutCfg = PORTRAIT_CFG;
    this._layoutBgCX = 0;
    this._layoutBgCY = 0;
    this._slideLayout = null;

    this.initialized = false;
    this._initialize();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // INITIALIZATION
  // ═══════════════════════════════════════════════════════════════════════════

  _initialize() {
    this.container.sortableChildren = true;

    this._createBlackBackground();
    this._createBackground();
    this._createLogoGlow();
    this._createLogo();
    this._createMaxWinText();
    this._createSlider();
    this._createTapText();

    this.initialized = true;
    this._updateLayout();
  }

  _createBlackBackground() {
    this._blackBg = new PIXI.Sprite(PIXI.Texture.WHITE);
    this._blackBg.tint = 0x000000;
    this._blackBg.zIndex = 0;
    this._blackBg.eventMode = "static";
    // Default (arrow) cursor — the background itself isn't a button, only the
    // "tap to continue" text and pagination dots show a hand cursor.
    this._blackBg.cursor = "default";
    // Full-screen swipe surface. Drag handlers are bound in _bindDragHandlers;
    // a clean tap (no drag) closes the popup via the swipe/tap split in
    // _onDragEnd. Pagination dots and the sound button sit on top as separate
    // interactive objects, so they intercept their own taps before the backdrop.
    this.container.addChild(this._blackBg);
  }

  _createBackground() {
    const isPortrait =
      this.dimensionsContext.orientation === PixiSettings.ORIENTATION.PORTRAIT;
    const tex = isPortrait ? this._bgPortraitTex : this._bgLandscapeTex;
    if (!tex) return;

    this._background = new PIXI.Sprite(tex);
    this._background.anchor.set(0.5);
    this._background.zIndex = 0.5;
    this._background.eventMode = "none";
    this.container.addChild(this._background);
  }

  /**
   * Glow behind the logo — zIndex 0.75 puts it above the background
   * (0.5) and below the logo (1), so it reads as light coming off the logo.
   */
  _createLogoGlow() {
    if (!this._logoGlowTex) return;

    this._logoGlow = new PIXI.Sprite(this._logoGlowTex);
    this._logoGlow.anchor.set(0.5);
    this._logoGlow.zIndex = 0.75;
    this._logoGlow.eventMode = "none";
    this.container.addChild(this._logoGlow);
  }

  _createLogo() {
    if (!this._logoTex) return;

    this._logo = new PIXI.Sprite(this._logoTex);
    this._logo.anchor.set(0.5);
    this._logo.zIndex = 1;
    this._logo.eventMode = "none";
    this.container.addChild(this._logo);
  }

  _createMaxWinText() {
    const text = `MAX WIN ${MAX_WIN_MULTIPLIER.toLocaleString("en-US")}X`;
    const style = new PIXI.TextStyle(
      scaleStyle(FONT_STYLES.winText, 24, { align: "center" }),
    );

    this._maxWinText = new PIXI.Text(text, style);
    this._maxWinText.anchor.set(0.5);
    this._maxWinText.resolution = 2;
    this._maxWinText.zIndex = 1;
    this._maxWinText.eventMode = "none";
    this.container.addChild(this._maxWinText);
  }

  _buildSlideContainer(def, name) {
    const container = new PIXI.Container();
    container.name = name;
    container.slideDef = def;

    const topTex = PIXI.Assets.get(def.topTex) || PIXI.Texture.EMPTY;
    const topSprite = new PIXI.Sprite(topTex);
    topSprite.anchor.set(0.5, 0.5);
    topSprite.eventMode = "none";
    container.addChild(topSprite);

    let bottomSprite = null;
    if (def.bottomTex) {
      const bottomTex = PIXI.Assets.get(def.bottomTex) || PIXI.Texture.EMPTY;
      bottomSprite = new PIXI.Sprite(bottomTex);
      bottomSprite.anchor.set(0.5, 0.5);
      bottomSprite.eventMode = "none";
      container.addChild(bottomSprite);
    }

    const descStr = this.t(def.descKey, { defaultValue: def.descDefault });
    const descText = new PIXI.Text(
      descStr,
      new PIXI.TextStyle({
        fontSize: 14,
        fill: 0xffffff,
        align: "center",
      }),
    );
    descText.anchor.set(0.5, 0);
    descText.resolution = 2;
    descText.eventMode = "none";
    container.addChild(descText);

    return { container, topSprite, bottomSprite, descText, def };
  }

  _createSlider() {
    this._sliderGroup = new PIXI.Container();
    this._sliderGroup.name = "slider_group";
    this._sliderGroup.zIndex = 1;
    this._sliderGroup.alpha = 0;
    this._sliderGroup.scale.set(0, 0);
    this.container.addChild(this._sliderGroup);

    this._slidesMaskContainer = new PIXI.Container();
    this._slidesMaskContainer.name = "slides_mask_container";
    // Non-interactive: the full-screen backdrop (_blackBg) is the swipe surface,
    // so this must not shadow its hit area. Masking still works regardless.
    this._slidesMaskContainer.eventMode = "none";
    this._sliderGroup.addChild(this._slidesMaskContainer);

    this._slidesMaskGfx = new PIXI.Graphics();
    this._slidesMaskContainer.addChild(this._slidesMaskGfx);
    this._slidesMaskContainer.mask = this._slidesMaskGfx;

    this._slidesTrack = new PIXI.Container();
    this._slidesTrack.name = "slides_track";
    this._slidesMaskContainer.addChild(this._slidesTrack);

    this._realSlides = SLIDE_DEFS.map((def, index) => {
      return this._buildSlideContainer(def, `intro_slide_${index}`);
    });

    this._rebuildTrackSlides();
    this._bindDragHandlers();
    this._createPaginationDots();
  }

  _rebuildTrackSlides() {
    if (!this._slidesTrack) return;

    this._slidesTrack.removeChildren();

    const cloneLast = this._buildSlideContainer(
      SLIDE_DEFS[TOTAL_SLIDES - 1],
      `intro_slide_clone_last`,
    );
    this._slidesTrack.addChild(cloneLast.container);

    for (const slide of this._realSlides) {
      this._slidesTrack.addChild(slide.container);
    }

    const cloneFirst = this._buildSlideContainer(
      SLIDE_DEFS[0],
      `intro_slide_clone_first`,
    );
    this._slidesTrack.addChild(cloneFirst.container);

    this._layoutTrackSlidePositions();
    this._syncTrackPosition(true);
  }

  _layoutTrackSlidePositions() {
    if (!this._slidesTrack || !this._slideWidth) return;

    for (let i = 0; i < this._slidesTrack.children.length; i++) {
      const child = this._slidesTrack.children[i];
      child.x = i * this._slideWidth;
      child.y = 0;
      this._layoutSlideContents(child);
    }
  }

  _computeSlideLayout(sw, sh, cfg) {
    const scale = SLIDE_CONTENT_SCALE;
    const imageOffsetY = sh * (cfg.slideImageOffsetYRatio ?? 0);
    // How wide the description may ever run. Per orientation, because portrait
    // has the slide to itself and landscape shares the row with the image.
    const descWordWrap = sw * (cfg.slideDescWrapRatio ?? 0.88);
    const layout = {
      cx: sw / 2,
      topCenterY: sh * cfg.slideTopCenterRatio + imageOffsetY,
      bottomCenterY: sh * cfg.slideBottomCenterRatio + imageOffsetY,
      descY: sh * cfg.slideDescTopRatio,
      topMaxW: sw * cfg.slideTopMaxWRatio * scale,
      topMaxH: sh * cfg.slideTopMaxHRatio * scale,
      bottomMaxW: sw * cfg.slideBottomMaxWRatio * scale,
      bottomMaxH: sh * cfg.slideBottomMaxHRatio * scale,
      singleCenterY: sh * cfg.slideSingleCenterRatio,
      singleMaxW: sw * cfg.slideSingleMaxWRatio * scale,
      singleMaxH: sh * cfg.slideSingleMaxHRatio * scale,
      descFontSize: this._fitDescFontSize(
        Math.round(
          (this._tapFontSize ?? Math.round(sw * cfg.tapFontRatio)) *
            (cfg.slideDescFontMult ?? 1),
        ),
        descWordWrap,
        cfg,
      ),
      descWordWrap,
    };

    if (cfg.slideSingleImageXRatio != null) {
      layout.singleRowCenterY = sh * cfg.slideSingleRowCenterRatio;
      layout.singleImageX = sw * cfg.slideSingleImageXRatio;
      layout.singleTextX = sw * cfg.slideSingleTextXRatio;
      layout.singleTextWrap = sw * cfg.slideSingleTextWrapRatio;
    }

    if (cfg.slidePairedRowCenterRatio != null) {
      layout.pairedRowCenterY =
        sh * cfg.slidePairedRowCenterRatio + imageOffsetY;
      layout.pairedRowMaxH = sh * cfg.slidePairedRowMaxHRatio * scale;
      layout.pairedLeftMaxW = sw * cfg.slidePairedLeftMaxWRatio * scale;
      layout.pairedRightMaxW = sw * cfg.slidePairedRightMaxWRatio * scale;
      layout.pairedGap = sw * cfg.slidePairedGapRatio;
    }

    return layout;
  }

  _getFitDimensions(texture, maxW, maxH) {
    const nativeW = texture?.width || maxW;
    const nativeH = texture?.height || maxH;
    if (!nativeW || !nativeH) return { w: maxW, h: maxH };

    const fitScale = Math.min(maxW / nativeW, maxH / nativeH);
    return { w: nativeW * fitScale, h: nativeH * fitScale };
  }

  _fitSpriteInBox(sprite, cx, cy, maxW, maxH, sizeMult = 1) {
    const { w, h } = this._getFitDimensions(sprite.texture, maxW, maxH);
    sprite.width = w * sizeMult;
    sprite.height = h * sizeMult;
    sprite.x = cx;
    sprite.y = cy;
    sprite.visible = true;
  }

  /**
   * The description size that fits EVERY slide, not just this one.
   *
   * ── Why one size for four slides ──
   *
   * The slides are a carousel, so the player sees them one after another. A
   * per-slide fit would make the words jump size on each swipe, which reads as
   * a fault rather than as layout. So the longest line on any slide decides,
   * and the other three simply sit inside it.
   *
   * ── Why fit at all, when wordWrap is on ──
   *
   * wordWrap only stops the text leaving the box; it does not stop it FILLING
   * the box. The Mad Labs copy is long — "MYSTERY REVEALS UNCOVER MATCHING
   * SYMBOLS," on its own is wider than a phone — so at the inherited size it
   * ran edge to edge and swallowed the card. Measuring with wrap OFF gives the
   * widest authored line, and scaling by the overflow brings it inside in one
   * step: text width is linear in font size.
   *
   * Landscape is left alone. There the text sits beside the image and is
   * styled against `singleTextWrap`, a different and much narrower width, so
   * fitting it to `descWordWrap` would shrink it against a box it never uses.
   *
   * @param {number} fontSize The inherited size, before any fit.
   * @param {number} maxWidth What descWordWrap allows.
   * @returns {number} fontSize, or the largest size that fits every slide.
   */
  _fitDescFontSize(fontSize, maxWidth, cfg) {
    if (!fontSize || !maxWidth) return fontSize;
    // Landscape — see above.
    if (cfg?.slideSingleImageXRatio != null) return fontSize;

    const style = new PIXI.TextStyle(
      scaleStyle(descStyleBase(), fontSize, { wordWrap: false }),
    );
    let widest = 0;
    for (const def of SLIDE_DEFS) {
      const str = this.t(def.descKey, { defaultValue: def.descDefault });
      widest = Math.max(widest, PIXI.TextMetrics.measureText(str, style).width);
    }
    if (widest <= maxWidth) return fontSize;
    return Math.max(8, Math.floor(fontSize * (maxWidth / widest)));
  }

  _getSlideCenterOffsetY(slideDef, slideHeight) {
    if (!slideDef || !slideHeight) return 0;
    const ratio = this._layoutIsPortrait
      ? slideDef.portraitCenterOffsetRatio
      : slideDef.landscapeCenterOffsetRatio;
    return slideHeight * (ratio ?? 0);
  }

  _getSlideSizeMult(slideDef) {
    if (!slideDef) return 1;
    if (this._layoutIsPortrait && slideDef.portraitSizeMult) {
      return slideDef.portraitSizeMult;
    }
    if (!this._layoutIsPortrait && slideDef.landscapeSizeMult) {
      return slideDef.landscapeSizeMult;
    }
    return slideDef.sizeMult ?? 1;
  }

  // Scale pot + coins together — sizeMult grows sprites but keeps the layout edge gap fixed.
  _layoutPairedSprites(
    topSprite,
    bottomSprite,
    layout,
    sizeMult,
    centerOffsetY = 0,
  ) {
    const topDims = this._getFitDimensions(
      topSprite.texture,
      layout.topMaxW,
      layout.topMaxH,
    );
    const botDims = this._getFitDimensions(
      bottomSprite.texture,
      layout.bottomMaxW,
      layout.bottomMaxH,
    );

    const topCY = layout.topCenterY;
    const topH = topDims.h * sizeMult;
    const botH = botDims.h * sizeMult;

    topSprite.width = topDims.w * sizeMult;
    topSprite.height = topH;
    topSprite.x = layout.cx;
    topSprite.y = topCY + centerOffsetY;
    topSprite.visible = true;

    // No extra design gap — sprites stack directly touching, relying on the
    // images' own internal padding for visual spacing (same technique as
    // PaytableOverlay's feature slides).
    bottomSprite.width = botDims.w * sizeMult;
    bottomSprite.height = botH;
    bottomSprite.x = layout.cx;
    bottomSprite.y = topCY + topH / 2 + botH / 2 + centerOffsetY;
    bottomSprite.visible = true;
  }

  // Landscape: pot left, coins right — centered as one row.
  _layoutPairedSpritesRow(
    topSprite,
    bottomSprite,
    layout,
    sizeMult,
    centerOffsetY = 0,
  ) {
    const leftDims = this._getFitDimensions(
      topSprite.texture,
      layout.pairedLeftMaxW,
      layout.pairedRowMaxH,
    );
    const rightDims = this._getFitDimensions(
      bottomSprite.texture,
      layout.pairedRightMaxW,
      layout.pairedRowMaxH,
    );

    const leftW = leftDims.w * sizeMult;
    const leftH = leftDims.h * sizeMult;
    const rightW = rightDims.w * sizeMult;
    const rightH = rightDims.h * sizeMult;
    const gap = layout.pairedGap;
    const totalW = leftW + gap + rightW;
    const rowCY = layout.pairedRowCenterY + centerOffsetY;
    const startX = layout.cx - totalW / 2;

    topSprite.width = leftW;
    topSprite.height = leftH;
    topSprite.x = startX + leftW / 2;
    topSprite.y = rowCY;
    topSprite.visible = true;

    bottomSprite.width = rightW;
    bottomSprite.height = rightH;
    bottomSprite.x = startX + leftW + gap + rightW / 2;
    bottomSprite.y = rowCY;
    bottomSprite.visible = true;
  }

  _layoutSlideContents(slideContainer) {
    const layout = this._slideLayout;
    if (!layout) return;

    const children = slideContainer.children;
    const hasBottom = children.length >= 3;
    const topSprite = children[0];
    const bottomSprite = hasBottom ? children[1] : null;
    const descText = hasBottom ? children[2] : children[1];
    if (!topSprite || !descText) return;

    // ── The gamble card's lettering, in plain white on black ──
    //
    // It used to be introTapToStart, which is the previous game's blue-on-blue
    // Zeus lettering and reads as borrowed against Mad Labs' art. The FACE is
    // now the gamble card's — Noto Sans 900, and the shadow's angle and
    // distance with it — spread from gambleCardStrip's own style so the family
    // and weight cannot drift from the cards.
    //
    // The colour is deliberately NOT the card's. The card sits on artwork and is
    // tinted to it (#fffadc on a dark brown shadow); these slides sit on a dim
    // room, where the same warm cream reads as dirty. Flat white is the most
    // legible thing there is and takes no side.
    //
    // ── An OUTLINE, not a drop shadow ──
    //
    // The same treatment the intro popup's MAX WIN gets, which is the other
    // large word on these screens: a black stroke all the way round rather than
    // an offset copy underneath. A shadow only separates the text from the
    // background on the side it falls, so a light patch of artwork on the other
    // side still swallows the letters. A stroke holds the edge everywhere, and
    // the two big texts on the same popup then read as one family.
    //
    // The thickness is win_text's own ratio — 6 against a 68px face — applied
    // to this face's 76, so the outline is the same WEIGHT relative to the
    // letters rather than the same pixel count at a different size. scaleStyle
    // then scales it with the layout's font size like every other pixel value.
    //
    // Stated explicitly rather than left to PIXI's default (which is black, and
    // is how win_text gets its outline without a `stroke` key). A default that
    // happens to be right is not a decision anyone can read.
    //
    // `dropShadow: false` because the card's shadow is still in the spread
    // above, and a stroke over a shadow is both at once.
    //
    // Overridden here rather than in gamble_freespins.json on purpose: that
    // file IS the cards, and restyling it would restyle the gamble screen.
    //
    // introTapToStart is deliberately left alone too: the Pot Collection
    // dialogs share it, and they are not this game.
    const base = descStyleBase();
    descText.style = new PIXI.TextStyle(
      scaleStyle(base, layout.descFontSize, {
        align: "center",
        wordWrap: true,
        wordWrapWidth: layout.descWordWrap,
      }),
    );
    descText.x = layout.cx;
    descText.y = layout.descY;
    descText.anchor.set(0.5, 0);

    if (!hasBottom) {
      if (bottomSprite) bottomSprite.visible = false;
      const sizeMult = this._getSlideSizeMult(slideContainer.slideDef);
      const centerOffsetY = this._getSlideCenterOffsetY(
        slideContainer.slideDef,
        this._slideHeight,
      );

      // Landscape: image left, text right, on one row. The image box and the
      // font size are identical to the vertical layout — only the two
      // positions and the text's wrap width change.
      if (!this._layoutIsPortrait && layout.singleImageX != null) {
        // Centred on the background, with the per-slide offset left as the
        // tuning knob. Falls back to the slide-area ratio if the background
        // centre is unavailable.
        const rowY =
          (layout.bgCenterY ?? layout.singleRowCenterY) + centerOffsetY;

        descText.style = new PIXI.TextStyle(
          scaleStyle(base, layout.descFontSize, {
            align: "center",
            wordWrap: true,
            wordWrapWidth: layout.singleTextWrap,
          }),
        );
        // Centred on the row rather than hanging from its top, so the text
        // block stays level with the middle of the image whatever its height.
        descText.anchor.set(0.5, 0.5);
        descText.x = layout.singleTextX;
        descText.y = rowY;

        this._fitSpriteInBox(
          topSprite,
          layout.singleImageX,
          rowY,
          layout.singleMaxW,
          layout.singleMaxH,
          sizeMult,
        );
        return;
      }

      // Portrait: the image sits on the BACKGROUND's centre, the same anchor
      // landscape uses, so it is never dragged around by the slide area's own
      // bounds.
      const singleY =
        (layout.bgCenterY ?? layout.singleCenterY) + centerOffsetY;

      if (layout.singleHeightFromBg > 0) {
        // Sized off the background height rather than fitted into the slide
        // box, then the text is hung off the image's real bottom edge — that
        // is what keeps the two apart at any image size.
        const tex = topSprite.texture;
        const aspect = (tex?.width || 1) / (tex?.height || 1);
        let h = layout.singleHeightFromBg;
        let w = h * aspect;
        if (layout.singleWidthCap > 0 && w > layout.singleWidthCap) {
          w = layout.singleWidthCap;
          h = w / aspect;
        }

        topSprite.width = w;
        topSprite.height = h;
        topSprite.x = layout.cx;
        topSprite.y = singleY;
        topSprite.visible = true;

        descText.anchor.set(0.5, 0);
        descText.x = layout.cx;
        descText.y = singleY + h / 2 + layout.singleTextGap;
        return;
      }

      this._fitSpriteInBox(
        topSprite,
        layout.cx,
        singleY,
        layout.singleMaxW,
        layout.singleMaxH,
        sizeMult,
      );
      return;
    }

    const sizeMult = this._getSlideSizeMult(slideContainer.slideDef);
    const centerOffsetY = this._getSlideCenterOffsetY(
      slideContainer.slideDef,
      this._slideHeight,
    );

    if (!this._layoutIsPortrait && layout.pairedRowCenterY != null) {
      this._layoutPairedSpritesRow(
        topSprite,
        bottomSprite,
        layout,
        sizeMult,
        centerOffsetY,
      );
      return;
    }

    this._layoutPairedSprites(
      topSprite,
      bottomSprite,
      layout,
      sizeMult,
      centerOffsetY,
    );
  }

  _bindDragHandlers() {
    // Bind to the full-screen backdrop so the whole popup is a swipe surface.
    const area = this._blackBg;
    if (!area) return;

    area.on("pointerdown", this._onDragStart.bind(this));
    // globalpointermove fires regardless of what's currently under the pointer,
    // so a swipe that passes over a dot or the sound button keeps tracking.
    area.on("globalpointermove", this._onDragMove.bind(this));
    area.on("pointerup", this._onDragEnd.bind(this));
    area.on("pointerupoutside", this._onDragEnd.bind(this));
    area.on("pointercancel", this._onDragEnd.bind(this));
  }

  _createPaginationDots() {
    this._dotsContainer = new PIXI.Container();
    this._dotsContainer.name = "pagination_dots";
    this._sliderGroup.addChild(this._dotsContainer);

    const activeTex =
      getNewUITexture(DOT_TEX_NAME_ACTIVE) || PIXI.Texture.WHITE;
    const inactiveTex =
      getNewUITexture(DOT_TEX_NAME_INACTIVE) || PIXI.Texture.WHITE;

    this._dotEntries = [];
    for (let i = 0; i < TOTAL_SLIDES; i++) {
      // Real sprite images, not drawn circles — avoids the jagged edges
      // PIXI Graphics produces on curves/border-radius shapes.
      const dot = new PIXI.Sprite(inactiveTex);
      dot.anchor.set(0.5);
      dot.eventMode = "static";
      dot.cursor = "pointer";
      dot.on("pointertap", (e) => {
        if (e?.stopPropagation) e.stopPropagation();
        soundManager.play(SOUND_NAME.button_click);
        this._goToSlide(i);
      });
      this._dotsContainer.addChild(dot);
      this._dotEntries.push({
        gfx: dot,
        index: i,
        activeTex,
        inactiveTex,
      });
    }
  }

  _createTapText() {
    this._tapContainer = new PIXI.Container();
    this._tapContainer.zIndex = 2;

    const text = this.t("tap_to_start").toUpperCase();
    // The shared tap word, same as every in-game popup. Only a placeholder
    // size — _updateTapTextStyle sets the real one once the layout is known.
    const style = new PIXI.TextStyle(
      scaleStyle(FONT_STYLES.tapToContinue, 24, { align: "center" }),
    );

    this._tapText = new PIXI.Text(text, style);
    this._tapText.anchor.set(0.5);
    this._tapText.resolution = 2;
    this._tapText.eventMode = "static";
    this._tapText.cursor = "pointer";

    this._tapText.on("pointerover", () => {
      if (this._hoverAnim) this._hoverAnim.pause();
      this._hoverAnim = anime({
        targets: this._tapContainer.scale,
        x: 1.08,
        y: 1.08,
        duration: 100,
        easing: "easeOutQuad",
      });
    });

    this._tapText.on("pointerout", () => {
      if (this._hoverAnim) this._hoverAnim.pause();
      this._hoverAnim = anime({
        targets: this._tapContainer.scale,
        x: 1.0,
        y: 1.0,
        duration: 100,
        easing: "easeOutQuad",
      });
    });

    this._tapText.on("pointertap", (e) => {
      if (e?.stopPropagation) e.stopPropagation();
      if (!this._isPressAnimating) {
        this._isPressAnimating = true;
        const origX = this._tapText.scale.x;
        const origY = this._tapText.scale.y;
        if (this._pressAnim) this._pressAnim.pause();
        this._pressAnim = anime({
          targets: this._tapText.scale,
          x: origX * 0.92,
          y: origY * 0.92,
          duration: 65,
          easing: "easeInQuad",
          complete: () => {
            this._pressAnim = anime({
              targets: this._tapText.scale,
              x: origX,
              y: origY,
              duration: 120,
              easing: "easeOutBack",
              complete: () => {
                this._isPressAnimating = false;
              },
            });
          },
        });
      }
      if (this._clickHandler) this._clickHandler(e);
    });

    this._tapContainer.addChild(this._tapText);
    // Hidden until the game finishes loading; the popi spine holds this slot
    // in the meantime. setGameLoaded(true) reveals it.
    this._tapContainer.visible = false;
    this.container.addChild(this._tapContainer);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // LOADING SPINE
  // ═══════════════════════════════════════════════════════════════════════════

  _createLoadingSpine() {
    if (!LOADING_SPINE_ENABLED) return;
    if (this._loadingSpine) return;
    if (!PIXI.Assets.cache.has(LOADING_SPINE_SKELETON_ALIAS)) return;

    try {
      this._loadingSpine = Spine.from({
        skeleton: LOADING_SPINE_SKELETON_ALIAS,
        atlas: LOADING_SPINE_ATLAS_ALIAS,
      });
    } catch {
      this._loadingSpine = null;
      return;
    }

    // ── Built HIDDEN, and shown only once it has a real layout ──
    //
    // This is the half of LoaderController's rule that was never copied over.
    // That screen builds its wordmark at alpha 0 and raises it inside
    // `_layoutArtwork`, precisely so a spine that has not been placed yet is
    // never seen. This one was built at alpha 1 and laid out afterwards, so
    // every way `_layoutLoadingSpine` can decline left a VISIBLE wordmark at
    // the skeleton origin — the top-left corner.
    //
    // There are two such ways and neither corrects itself: the bounds
    // measurement can fail, and `setGameLoaded(false)` can arrive before
    // `_updateLayout` has ever run, in which case `_layoutBgCX` and
    // `_tapTextY` are still 0 and the `|| 0` fallbacks place it at the origin
    // on purpose. Nothing runs the layout again unless the player resizes,
    // which is why it stuck.
    this._loadingSpine.alpha = 0;
    this._loadingSpine.zIndex = 3;
    this._loadingSpine.eventMode = "none";
    this.container.addChild(this._loadingSpine);

    const animations = this._loadingSpine.skeleton?.data?.animations;
    if (animations?.length) {
      const entry = this._loadingSpine.state.setAnimation(
        0,
        animations[0].name,
        true,
      );
      // Continue the loader's revolution rather than starting a new one. Both
      // "last" pairs move with it, otherwise the next update would treat the
      // skipped span as freshly played and fire every event in it — including
      // the `complete` that awaitLoadingSpineComplete() is listening for.
      const startAt = takeHandoverTrackTime();
      if (entry && startAt > 0) {
        entry.trackTime = startAt;
        entry.trackLast = startAt;
        entry.nextTrackLast = startAt;
        entry.setAnimationLast?.(startAt);
      }
    }

    this._retryLoadingSpineLayout();
  }

  /**
   * Lay it out, and keep trying until that actually works.
   *
   * The loader gets its retry for free — its artwork lands asynchronously and
   * runs the layout again. Nothing re-runs this one, so the retry is explicit:
   * one attempt per frame until a layout succeeds, then the spine is shown.
   *
   * Bounded, because a rig that can never be measured must not hold a rAF for
   * the life of the screen. On giving up the spine is shown anyway — the same
   * call the loader makes in its own failure path, and for the same reason:
   * badly placed beats a loading screen with nothing on it.
   */
  _retryLoadingSpineLayout(attempt = 0) {
    this._loadingSpineLayoutRAF = null;
    if (!this._loadingSpine) return;

    // Shows it on success — see the end of _layoutLoadingSpine.
    if (this._layoutLoadingSpine()) return;

    if (attempt >= LOADING_SPINE_LAYOUT_MAX_FRAMES) {
      console.warn(
        "[IntroPopupComposed] loading spine never got a layout — showing it unplaced.",
      );
      this._loadingSpine.alpha = 1;
      return;
    }

    this._loadingSpineLayoutRAF = requestAnimationFrame(() =>
      this._retryLoadingSpineLayout(attempt + 1),
    );
  }

  /**
   * @returns {boolean} true only when the wordmark was actually placed.
   */
  _layoutLoadingSpine() {
    if (!this._loadingSpine) return false;
    const spine = this._loadingSpine;

    // ── The layout must have RUN, not merely have defaults ──
    //
    // `_layoutBgCX` and `_tapTextY` start at 0, and 0 is a legitimate-looking
    // number that puts the wordmark in the corner. A real background centre is
    // never 0 on a real viewport, so this separates "not measured yet" from
    // "measured", which the `|| 0` fallbacks below cannot do on their own.
    if (!(this._layoutBgCX > 0)) return false;

    // Measure once and reuse — the skeleton's proportions never change, and
    // this runs on every resize. A FAILED measurement is not cached: an empty
    // box would stick forever and leave the wordmark unscaled on the skeleton
    // origin, far below the popup, on this device for the whole session.
    if (!this._loadingSpineBounds) {
      this._loadingSpineBounds = measureLoadingSpineBounds(
        spine,
        LOADING_SPINE_POSE_TIME,
      );
    }

    const bounds = this._loadingSpineBounds;
    if (!bounds) return false; // nothing measurable yet — retried by the caller
    // Shared with the loader so both screens render the wordmark identically.
    const targetHeight =
      getLoadingSpineTargetHeight(this.dimensionsContext) ||
      (this._tapFontSize || 24) * LOADING_SPINE_HEIGHT_MULT;
    const scale = targetHeight / bounds.height;
    spine.scale.set(scale);

    const centreX = bounds.x + bounds.width / 2;
    const centreY = bounds.y + bounds.height / 2;
    spine.x = (this._layoutBgCX || 0) - centreX * scale;
    spine.y = (this._tapTextY || this._layoutBgCY || 0) - centreY * scale;

    // Shown HERE, not by the retry, so every route in reveals it — including
    // `_updateLayout`, which runs on resize and is what rescues a spine that
    // was still waiting when the player turned the device.
    spine.alpha = 1;
    return true;
  }

  /**
   * Resolve once the wordmark has finished its current revolution, then hold
   * it on the final frame.
   *
   * Cutting the animation off mid-rotation looks abrupt, so the caller waits
   * for this before mounting the game and swapping in the tap text. Holding
   * the last frame afterwards means the 270-360ms mount lands on a static
   * image rather than a moving one.
   *
   * Resolves immediately when there is no spine (disabled, or creation
   * failed), and is capped so a missing complete event can never hang the
   * player on the intro screen.
   */
  awaitLoadingSpineComplete() {
    return new Promise((resolve) => {
      const spine = this._loadingSpine;
      if (!spine?.state) {
        resolve();
        return;
      }

      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        if (this._spineCompleteTimeout) {
          this._spineCompleteTimeout.clear();
          this._spineCompleteTimeout = null;
        }
        try {
          spine.state.removeListener(listener);
          spine.state.timeScale = 0; // hold the final frame
        } catch {
          // Silent fail — worst case it keeps looping until destroyed.
        }
        resolve();
      };

      const listener = { complete: finish };
      spine.state.addListener(listener);
      this._spineCompleteTimeout = new TickerTimeout(
        finish,
        LOADING_SPINE_COMPLETE_MAX_WAIT_MS,
      );
    });
  }

  _destroyLoadingSpine() {
    this._loadingSpineBounds = null;
    if (this._loadingSpineLayoutRAF !== null) {
      cancelAnimationFrame(this._loadingSpineLayoutRAF);
      this._loadingSpineLayoutRAF = null;
    }
    if (this._spineCompleteTimeout) {
      this._spineCompleteTimeout.clear();
      this._spineCompleteTimeout = null;
    }
    if (!this._loadingSpine) return;
    try {
      this._loadingSpine.state?.clearListeners();
      this._loadingSpine.state?.clearTracks();
      if (this.container?.children?.includes(this._loadingSpine)) {
        this.container.removeChild(this._loadingSpine);
      }
      this._loadingSpine.destroy();
    } catch {
      // Silent fail
    }
    this._loadingSpine = null;
  }

  /**
   * Swap between "still loading" and "ready to play".
   *
   * While loading, the popi spine sits where the tap text goes and the tap
   * text is hidden — the player can read and swipe the slider, but can't enter
   * a game that isn't there yet. Once loaded the spine is destroyed and the
   * tap text takes its place.
   */
  setGameLoaded(isLoaded) {
    if (this._isGameLoaded === isLoaded) return;
    this._isGameLoaded = isLoaded;

    if (isLoaded) {
      this._destroyLoadingSpine();
      this._revealTapText();
      return;
    }

    if (this._tapContainer) this._tapContainer.visible = false;
    this._createLoadingSpine();
  }

  /**
   * Fade + scale the tap text in as it takes over from the loading spine —
   * same easing/durations playIntro() uses for the slider, so the two entrances
   * read as one design rather than the text just popping into existence.
   */
  _revealTapText() {
    // ── Fails OPEN ──
    //
    // No tap word means there is nothing to wait for, and a gate that can never
    // open would trap the player on the popup with no way into the game. The
    // only safe direction for this flag when the text is absent is `true`.
    if (!this._tapContainer) {
      this._tapRevealComplete = true;
      return;
    }

    if (this._tapRevealAnims) this._tapRevealAnims.forEach((a) => a.pause());
    this._tapRevealAnims = [];
    this._tapRevealComplete = false;

    this._tapContainer.visible = true;
    this._tapContainer.alpha = 0;
    this._tapContainer.scale.set(0, 0);

    this._tapRevealAnims.push(
      anime({
        targets: this._tapContainer,
        alpha: 1,
        duration: 500,
        delay: 25,
        easing: "easeOutCubic",
      }),
    );

    this._tapRevealAnims.push(
      anime({
        targets: this._tapContainer.scale,
        x: 1,
        y: 1,
        duration: 600,
        delay: 25,
        easing: "easeOutCubic",
        // ── The LONGER of the two, which is what "fully arrived" means ──
        //
        // The fade ends at 525ms and the scale at 625ms, so keying the flag to
        // the fade would open the gate while the word was still growing. This
        // is the one that finishes last, so when it completes the tap word is
        // at full opacity, at full size, and has stopped moving.
        complete: () => {
          this._tapRevealComplete = true;
        },
      }),
    );
  }

  /**
   * Is the tap word fully on screen and still?
   *
   * The popup used to open its own door on `gameFullyLoaded`, which is the
   * instant the reveal STARTS. That left 625ms in which the game could be
   * entered by tapping a word that was still fading and growing — or before it
   * had drawn at all, since the whole backdrop is a tap surface. Vladimir:
   * he wants to be certain the game is loaded AND ready, and the word settling
   * is the only thing on screen that says so.
   */
  isTapReady() {
    return this._tapRevealComplete === true;
  }

  isGameLoaded() {
    return this._isGameLoaded;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SLIDER NAVIGATION
  // ═══════════════════════════════════════════════════════════════════════════

  _getRealSlideIndex() {
    if (this._currentSlide < 0) return TOTAL_SLIDES - 1;
    if (this._currentSlide >= TOTAL_SLIDES) return 0;
    return this._currentSlide;
  }

  _getTargetTrackX(slideIndex) {
    return -((slideIndex + 1) * this._slideWidth);
  }

  _setTrackX(x) {
    this._trackPos.x = x;
    if (this._slidesTrack) {
      this._slidesTrack.x = x;
    }
    this._updateSlideOpacities();
  }

  _calculateSlideAlpha(slideGlobalX) {
    const slideWidth = this._slideWidth;
    if (!slideWidth) return 1;
    const distanceFromCenter = Math.abs(slideGlobalX);
    const fadeStartDistance = slideWidth * 0.1;
    const fadeEndDistance = slideWidth * 0.35;
    if (distanceFromCenter <= fadeStartDistance) return 1;
    if (distanceFromCenter >= fadeEndDistance) return 0;
    const t =
      (distanceFromCenter - fadeStartDistance) /
      (fadeEndDistance - fadeStartDistance);
    return 1 - this._fadeEasing(t);
  }

  _updateSlideOpacities() {
    if (!this._slidesTrack || !this._slideWidth) return;
    const trackX = this._trackPos.x;
    // child.x is left edge; measure distance of slide center from mask center
    const maskCenterX = this._slideWidth / 2;

    for (let i = 0; i < this._slidesTrack.children.length; i++) {
      const child = this._slidesTrack.children[i];
      const slideCenterX = trackX + child.x + this._slideWidth / 2;
      child.alpha = this._calculateSlideAlpha(slideCenterX - maskCenterX);
    }
  }

  _cancelTrackAnim() {
    if (this._trackAnim) {
      anime.remove(this._trackPos);
      this._trackAnim = null;
    }
  }

  _syncTrackPosition(isJump) {
    const realIndex = this._getRealSlideIndex();
    if (isJump) {
      this._isJumping = true;
      this._currentSlide = realIndex;
    }
    this._cancelTrackAnim();
    this._setTrackX(this._getTargetTrackX(realIndex));
    if (isJump) {
      this._isJumping = false;
      this._isTransitioning = false;
    }
    this._updatePaginationDots();
  }

  _animateToSlide(nextSlideIndex) {
    if (!this._slidesTrack || !this._slideWidth) return;

    const targetX = this._getTargetTrackX(nextSlideIndex);
    this._cancelTrackAnim();

    if (this._isJumping) {
      this._setTrackX(targetX);
      this._isJumping = false;
      this._isTransitioning = false;
      return;
    }

    const duration = this._isPaginationNav ? 500 : 700;
    this._isPaginationNav = false;

    this._trackAnim = anime({
      targets: this._trackPos,
      x: targetX,
      duration,
      easing: "cubicBezier(.34, 1.30, .64, 1)",
      update: () => {
        if (this._slidesTrack) {
          this._slidesTrack.x = this._trackPos.x;
        }
        this._updateSlideOpacities();
      },
      complete: () => {
        this._trackAnim = null;
        if (this._currentSlide >= TOTAL_SLIDES) {
          this._isJumping = true;
          this._currentSlide = 0;
          this._animateToSlide(0);
        } else if (this._currentSlide < 0) {
          this._isJumping = true;
          this._currentSlide = TOTAL_SLIDES - 1;
          this._animateToSlide(TOTAL_SLIDES - 1);
        } else {
          this._isTransitioning = false;
          this._updatePaginationDots();
        }
      },
    });
  }

  _goNext() {
    if (this._isTransitioning || this._isDragging) return;
    const now = Date.now();
    if (now - this._lastNavTime < NAV_THROTTLE_MS) return;
    this._lastNavTime = now;
    this._resetAutoplayTimer();
    this._isTransitioning = true;
    this._currentSlide += 1;
    this._updatePaginationDots();
    this._animateToSlide(this._currentSlide);
  }

  _goPrev() {
    if (this._isTransitioning || this._isDragging) return;
    const now = Date.now();
    if (now - this._lastNavTime < NAV_THROTTLE_MS) return;
    this._lastNavTime = now;
    this._resetAutoplayTimer();
    this._isTransitioning = true;
    this._currentSlide -= 1;
    this._updatePaginationDots();
    this._animateToSlide(this._currentSlide);
  }

  _goToSlide(index) {
    if (this._isDragging || this._isJumping) return;
    if (index === this._getRealSlideIndex()) return;
    const now = Date.now();
    if (now - this._lastNavTime < NAV_THROTTLE_MS) return;
    this._lastNavTime = now;
    this._resetAutoplayTimer();

    this._cancelTrackAnim();
    const targetX = this._getTargetTrackX(index);
    const direction = index > this._getRealSlideIndex() ? 1 : -1;
    const offsetX = targetX + direction * this._slideWidth * 0.35;
    this._setTrackX(offsetX);

    this._isPaginationNav = true;
    this._isTransitioning = true;
    this._currentSlide = index;
    this._updatePaginationDots();
    this._animateToSlide(index);
  }

  _onDragStart(e) {
    if (this._isTransitioning) return;
    this._isDragging = true;
    this._dragStartX = e.global.x;
    this._dragCurrentX = e.global.x;
    this._dragStartTime = Date.now();
    this._dragStartTrackX = this._trackPos.x;
    this._cancelTrackAnim();
    this._resetAutoplayTimer();
  }

  _onDragMove(e) {
    if (!this._isDragging) return;
    this._dragCurrentX = e.global.x;
    let dragDelta = this._dragCurrentX - this._dragStartX;
    // Clamp travel to one slide width per gesture — the track can only ever
    // reveal the adjacent slide, never scroll past by more than one.
    const maxDelta = this._slideWidth || Infinity;
    if (dragDelta > maxDelta) dragDelta = maxDelta;
    else if (dragDelta < -maxDelta) dragDelta = -maxDelta;
    this._setTrackX(this._dragStartTrackX + dragDelta);
  }

  _onDragEnd(e) {
    if (!this._isDragging) return;

    const dragDistance = this._dragCurrentX - this._dragStartX;
    const dragDuration = Date.now() - this._dragStartTime;
    const velocity = Math.abs(dragDistance / Math.max(dragDuration, 1));

    this._isDragging = false;

    if (Math.abs(dragDistance) <= TAP_MOVE_THRESHOLD) {
      // Clean tap (no real drag) → dismiss popup + fullscreen (same as before).
      // Checked first so a fast tap can't be misread as a flick by velocity.
      if (this._clickHandler) this._clickHandler(e);
    } else if (
      Math.abs(dragDistance) > SWIPE_THRESHOLD ||
      velocity > VELOCITY_THRESHOLD
    ) {
      // Deliberate swipe → navigate (does not close). We're touching the screen,
      // so also request fullscreen — same as a tap, minus the dismiss.
      if (this._fullscreenHandler) this._fullscreenHandler();
      this._resetAutoplayTimer();
      this._isTransitioning = true;
      if (dragDistance > 0) {
        this._currentSlide -= 1;
      } else {
        this._currentSlide += 1;
      }
      this._updatePaginationDots();
      this._animateToSlide(this._currentSlide);
    } else {
      // Indecisive drag (moved, but below swipe threshold) → snap back, no close.
      this._isTransitioning = true;
      this._trackAnim = anime({
        targets: this._trackPos,
        x: this._getTargetTrackX(this._getRealSlideIndex()),
        duration: 300,
        easing: "cubicBezier(.34, 1.30, .64, 1)",
        update: () => {
          if (this._slidesTrack) {
            this._slidesTrack.x = this._trackPos.x;
          }
          this._updateSlideOpacities();
        },
        complete: () => {
          this._trackAnim = null;
          this._isTransitioning = false;
        },
      });
    }
  }

  _updatePaginationDots(bgW = this._layoutBgW, cfg = this._layoutCfg) {
    if (!this._dotEntries.length || !bgW || !cfg) return;

    const activeIndex = this._getRealSlideIndex();
    const dotSize = Math.round(bgW * cfg.dotSizeRatio * 1.1);
    const dotGap = Math.round(bgW * cfg.dotGapRatio * 3.3);
    const totalW = TOTAL_SLIDES * dotSize + (TOTAL_SLIDES - 1) * dotGap;
    const startX = -totalW / 2 + dotSize / 2;

    for (let i = 0; i < this._dotEntries.length; i++) {
      const { gfx, activeTex, inactiveTex } = this._dotEntries[i];
      const isActive = i === activeIndex;

      gfx.texture = isActive ? activeTex : inactiveTex;
      // Explicit width/height (not native texture size) — both circles must
      // render at the exact same size regardless of their source dimensions.
      gfx.width = dotSize;
      gfx.height = dotSize;
      gfx.x = startX + i * (dotSize + dotGap);
      gfx.y = 0;
      // hitArea lives in the sprite's LOCAL (pre-scale) space. Setting
      // width/height above scales the sprite by dotSize/textureSize, so a
      // local dotSize-radius circle collapses to a near-zero on-screen target
      // for large source textures (active vs inactive dots differ). Divide by
      // the sprite scale so every dot gets the same generous on-screen tap
      // zone (~one dot diameter of padding) no matter which texture it uses.
      const sx = Math.abs(gfx.scale.x) || 1;
      const sy = Math.abs(gfx.scale.y) || 1;
      const hx = dotSize / sx;
      const hy = dotSize / sy;
      gfx.hitArea = new PIXI.Rectangle(-hx, -hy, hx * 2, hy * 2);
    }
  }

  // Advances one slide every AUTOPLAY_INTERVAL_MS. Uses the shared PIXI ticker
  // (deltaMS accumulation) instead of setInterval/setTimeout, matching the
  // timing pattern used by every other animated controller in this game.
  _startAutoplay() {
    if (!SLIDER_AUTOPLAY_ENABLED) return;
    if (this._autoplayTickerCallback) return;

    this._autoplayElapsed = 0;
    this._autoplayTickerCallback = () => {
      if (this._isDragging || this._isTransitioning) return;

      this._autoplayElapsed += PIXI.Ticker.shared.deltaMS;
      if (this._autoplayElapsed >= AUTOPLAY_INTERVAL_MS) {
        this._autoplayElapsed = 0;
        this._goNext();
      }
    };
    PIXI.Ticker.shared.add(this._autoplayTickerCallback);
  }

  _stopAutoplay() {
    if (!this._autoplayTickerCallback) return;
    PIXI.Ticker.shared.remove(this._autoplayTickerCallback);
    this._autoplayTickerCallback = null;
  }

  // Restarts the 3s countdown — called on any manual navigation so autoplay
  // doesn't immediately fire right after the user just moved the slider.
  _resetAutoplayTimer() {
    this._autoplayElapsed = 0;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PUBLIC API
  // ═══════════════════════════════════════════════════════════════════════════

  // Keyboard navigation (ArrowLeft / ArrowRight from the React shell). These
  // reuse the same throttle + transition guards + infinite-loop wrap as swipes.
  nextSlide() {
    this._goNext();
  }

  prevSlide() {
    this._goPrev();
  }

  setClickHandler(handler) {
    this._clickHandler = handler;
  }

  setFullscreenHandler(handler) {
    this._fullscreenHandler = handler;
  }

  playIntro() {
    if (!this._sliderGroup) return;

    if (this._introAnims) this._introAnims.forEach((a) => a.pause());
    this._introAnims = [];

    this._currentSlide = 0;
    this._syncTrackPosition(true);

    this._sliderGroup.alpha = 0;
    this._sliderGroup.scale.set(0, 0);

    this._introAnims.push(
      anime({
        targets: this._sliderGroup,
        alpha: 1,
        duration: 500,
        delay: 25,
        easing: "easeOutCubic",
      }),
    );

    this._introAnims.push(
      anime({
        targets: this._sliderGroup.scale,
        x: 1,
        y: 1,
        duration: 600,
        delay: 25,
        easing: "easeOutCubic",
      }),
    );

    this._startAutoplay();
  }

  updateDimensions(dimensionsContext) {
    this.dimensionsContext = dimensionsContext;
    if (this.initialized) {
      this._updateLayout();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // LAYOUT
  // ═══════════════════════════════════════════════════════════════════════════

  _updateLayout() {
    const { dimensions, device, orientation, uiSymbolSize, isMobileSmall } =
      this.dimensionsContext;
    if (!dimensions) return;

    const isPortrait = orientation === PixiSettings.ORIENTATION.PORTRAIT;
    const { width, height } = dimensions;

    const layoutCfg = getLayoutConfig(device, isPortrait, isMobileSmall, width);
    const pad = uiSymbolSize * layoutCfg.padMult;
    const sidePad = uiSymbolSize * layoutCfg.sidePadMult;
    const topEdge = pad;
    const botEdge = height - pad;
    const contentH = botEdge - topEdge;
    const contentW = width - 2 * sidePad;
    const contentCY = topEdge + contentH / 2;

    if (this._blackBg) {
      this._blackBg.x = 0;
      this._blackBg.y = 0;
      this._blackBg.width = width;
      this._blackBg.height = height;
    }

    const bgTex = isPortrait ? this._bgPortraitTex : this._bgLandscapeTex;
    let bgW;
    let bgH;
    let bgCX;
    let bgCY;

    if (this._background && bgTex) {
      this._background.texture = bgTex;
      const bgScaleMult = layoutCfg.bgScaleMult;
      const bgNativeW = bgTex.width || BG_NATIVE_W;
      const bgNativeH = bgTex.height || BG_NATIVE_H;
      const bgScale =
        Math.min(contentW / bgNativeW, contentH / bgNativeH) * bgScaleMult;
      bgW = bgNativeW * bgScale;
      bgH = bgNativeH * bgScale;
      bgCX = width / 2;
      bgCY = contentCY;

      this._background.x = bgCX;
      this._background.y = bgCY;
      this._background.width = bgW;
      this._background.height = bgH;
    } else {
      bgW = width;
      bgH = height;
      bgCX = width / 2;
      bgCY = contentCY;
    }

    const cfg = isPortrait ? PORTRAIT_CFG : LANDSCAPE_CFG;
    this._layoutIsPortrait = isPortrait;
    this._layoutBgW = bgW;
    this._layoutCfg = cfg;
    this._layoutBgCX = bgCX;
    this._layoutBgCY = bgCY;

    let logoW = 0;
    let logoH = 0;
    let logoY = bgCY;

    if (this._logo && this._logoTex) {
      logoW = bgW * cfg.logoWRatio;
      logoH = logoW / (this._logoTex.width / this._logoTex.height);
      logoY = bgCY + bgH * cfg.logoYRatio;
      this._logo.x = bgCX;
      this._logo.y = logoY;
      this._logo.width = logoW;
      this._logo.height = logoH;
    }

    // Must follow the logo — the glow is sized from the logo's final width,
    // and keeps its own aspect so it can never stretch.
    if (this._logoGlow && this._logoGlowTex && logoW > 0) {
      const glowW = logoW * cfg.glowScaleVsLogo;
      const glowAspect =
        (this._logoGlowTex.width || 1) / (this._logoGlowTex.height || 1);
      this._logoGlow.x = bgCX;
      this._logoGlow.y = logoY + logoH * cfg.glowYOffsetVsLogo;
      this._logoGlow.width = glowW;
      this._logoGlow.height = glowW / glowAspect;
    }

    if (this._maxWinText) {
      const maxWinBudgetW = bgW * 0.85;
      const maxWinTextStr = this._maxWinText.text;
      const base = FONT_STYLES.winText;
      let fontSize = Math.round(bgW * cfg.maxWinFontRatio);
      const tempStyle = new PIXI.TextStyle(scaleStyle(base, fontSize));
      const metrics = PIXI.TextMetrics.measureText(maxWinTextStr, tempStyle);
      if (metrics.width > maxWinBudgetW) {
        fontSize = Math.round(fontSize * (maxWinBudgetW / metrics.width));
      }
      this._maxWinText.style = new PIXI.TextStyle(
        scaleStyle(base, fontSize, { align: "center" }),
      );
      this._maxWinText.x = width / 2;
      this._maxWinText.y = bgCY + bgH * cfg.maxWinYRatio;
    }

    this._updateTapTextStyle(bgW, bgH, bgCY, cfg, width);
    this._updateSlider(isPortrait, bgW, bgH, bgCX, bgCY, cfg);
  }

  _computeLandscapeContentBottomY(bgW, bgH, bgCY, cfg) {
    const cardW = bgW * (cfg.cardWRatio ?? 0.29);
    // The card's aspect. It used to be read off `intro_feature_mystery_pot`,
    // a Zeus texture this game no longer ships — and the fallback beside it
    // (542x746) did not even match that file (572x399), so the two disagreed
    // depending on whether the asset had loaded. Stated once, from the slide
    // art this popup actually draws.
    const cardH = cardW / (CARD_ASPECT_W / CARD_ASPECT_H);
    const centerY = bgCY + bgH * (cfg.contentCenterYRatio ?? 0.08);
    return centerY + cardH / 2;
  }

  _resolveTapTextY(bgH, bgCY, cfg) {
    if (cfg.tapYRatio != null) {
      return bgCY + bgH * cfg.tapYRatio;
    }

    const contentBottomY = this._computeLandscapeContentBottomY(
      this._layoutBgW,
      bgH,
      bgCY,
      cfg,
    );
    this._contentBottomY = contentBottomY;
    const tapOffsetY = bgH * (cfg.tapYOffsetRatio ?? 0);
    return contentBottomY + bgH * cfg.tapGapRatio + tapOffsetY;
  }

  _updateTapTextStyle(bgW, bgH, bgCY, cfg, screenWidth) {
    if (!this._tapContainer || !this._tapText) return;

    const tapFontSize = Math.round(bgW * cfg.tapFontRatio);
    // Matches the tap-to-continue word on every in-game popup. The size is
    // still the intro's own ratio-of-background, because this one sits on a
    // full-screen composed layout rather than in a spine bone.
    const base = FONT_STYLES.tapToContinue;
    const tapTextStr = this._tapText.text;
    const tempStyle = new PIXI.TextStyle(scaleStyle(base, tapFontSize));
    const metrics = PIXI.TextMetrics.measureText(tapTextStr, tempStyle);
    const finalFontSize =
      metrics.width > screenWidth * 0.9
        ? Math.round(tapFontSize * ((screenWidth * 0.9) / metrics.width))
        : tapFontSize;

    this._tapText.style = new PIXI.TextStyle(
      scaleStyle(base, finalFontSize, { align: "center" }),
    );
    this._tapFontSize = finalFontSize;
    this._tapTextY = this._resolveTapTextY(bgH, bgCY, cfg);
  }

  _updateSlider(isPortrait, bgW, bgH, bgCX, bgCY, cfg) {
    if (!this._slidesMaskContainer || !this._slidesTrack) return;

    const prevSlideWidth = this._slideWidth;
    const prevRealIndex = this._getRealSlideIndex();

    const maxWinBottom = this._maxWinText
      ? this._maxWinText.y + this._maxWinText.height / 2
      : bgCY + bgH * cfg.maxWinYRatio;
    const tapTextH =
      this._tapText?.height ?? Math.round(bgW * cfg.tapFontRatio);
    const dotSize = Math.round(bgW * cfg.dotSizeRatio * 1.1);

    this._tapTextY = this._resolveTapTextY(bgH, bgCY, cfg);
    // Placed directly when the config gives the dots their own Y; otherwise
    // they hang above the tap text by dotsGapRatio, as they always did.
    this._dotsCenterY =
      cfg.dotsYRatio != null
        ? bgCY + bgH * cfg.dotsYRatio
        : this._tapTextY - tapTextH / 2 - bgH * cfg.dotsGapRatio - dotSize / 2;

    const slideAreaTop = maxWinBottom + bgH * cfg.slideTopGapRatio;
    const slideBottomPad = bgH * (cfg.slideAreaBottomPadRatio ?? 0.015);
    const slideAreaBottomFromDots =
      this._dotsCenterY - dotSize / 2 - slideBottomPad;
    const slideAreaBottom =
      cfg.slideAreaBottomRatio != null
        ? bgCY + bgH * cfg.slideAreaBottomRatio
        : slideAreaBottomFromDots;
    const slideAreaHeight = Math.max(40, slideAreaBottom - slideAreaTop);
    const slideAreaWidth = bgW * 0.94;
    const slideAreaX = bgCX - slideAreaWidth / 2;

    this._slideWidth = slideAreaWidth;
    this._slideHeight = slideAreaHeight;
    this._slideLayout = this._computeSlideLayout(
      slideAreaWidth,
      slideAreaHeight,
      cfg,
    );

    // The background's centre, expressed in slide-local coordinates (the mask
    // container is placed at slideAreaTop, so that is the slides' y = 0).
    //
    // The horizontal single-image row anchors to THIS, not to a fraction of
    // the slide area. The slide area starts below the max-win text and ends
    // above the dots, so its own centre is nowhere near the background's —
    // which is why the row and its text sat high.
    this._slideLayout.bgCenterY = bgCY - slideAreaTop;

    if (cfg.slideSingleHeightVsBgRatio != null) {
      this._slideLayout.singleHeightFromBg =
        bgH * cfg.slideSingleHeightVsBgRatio;
      this._slideLayout.singleTextGap =
        bgH * (cfg.slideSingleTextGapRatio ?? 0);
      // Never wider than the slide itself, whatever the image's aspect.
      this._slideLayout.singleWidthCap = slideAreaWidth * 0.98;
    }

    // Mask clips on Y only: it keeps its tight vertical bounds (so slides never
    // bleed into the max-win text or dots) but overscans far on X so the sides
    // are never hard-cut — the per-slide opacity fade handles left/right
    // transitions naturally as slides enter and leave.
    const maskOverscanX =
      this.dimensionsContext?.dimensions?.width || slideAreaWidth * 3;
    this._slidesMaskGfx.clear();
    this._slidesMaskGfx.beginFill(0xffffff);
    this._slidesMaskGfx.drawRect(
      -maskOverscanX,
      0,
      slideAreaWidth + maskOverscanX * 2,
      slideAreaHeight,
    );
    this._slidesMaskGfx.endFill();

    this._slidesMaskContainer.x = slideAreaX;
    this._slidesMaskContainer.y = slideAreaTop;

    // Track slides (incl. clones) are built once in _createSlider — on resize we
    // only reposition/refit them. Rebuilding here would re-create the clone
    // containers every resize without destroying the old ones (a leak).
    this._layoutTrackSlidePositions();

    this._currentSlide = prevRealIndex;
    if (prevSlideWidth !== this._slideWidth) {
      this._cancelTrackAnim();
    }
    this._syncTrackPosition(true);

    if (this._dotsContainer) {
      this._dotsContainer.x = bgCX;
      this._dotsContainer.y = this._dotsCenterY;
      this._updatePaginationDots(bgW, cfg);
    }

    if (this._sliderGroup) {
      this._sliderGroup.pivot.set(bgCX, bgCY);
      this._sliderGroup.x = bgCX;
      this._sliderGroup.y = bgCY;
    }

    if (this._tapContainer && this._tapText) {
      // The shared nudge, per layout, applied to the WORD and not to
      // `_tapTextY` — the pagination dots hang off that line and are correctly
      // spaced already. `layout` is the resolved LAYOUT_TYPES key, the same one
      // every other responsive table in this game keys on. See
      // tapWordAnchor.TAP_WORD_NUDGE.
      const tapY =
        this._tapTextY +
        tapWordNudge(this._tapFontSize, this.dimensionsContext?.layout);

      this._tapContainer.x = bgCX;
      this._tapContainer.y = tapY;

      // ── This popup is the REFERENCE for every other one ──
      //
      // Mina, demo 2: "Make 'TAP TO START/CONTINUE' same size and position on
      // all screens. Copy from start page." The spine popups cannot recompute
      // this line — it comes from this popup's background fit, and in landscape
      // from the bottom of the slide card — so what was measured is published
      // and they solve back to it at their own scale. See utils/tapWordAnchor.
      //
      // Published from HERE, the last word on both numbers: `_tapTextY` is
      // recomputed after the pagination dots are placed, and `_tapFontSize` is
      // the size AFTER the long-translation shrink. Publishing from
      // _updateTapTextStyle would hand out the value before either.
      publishTapWordAnchor({
        y: tapY,
        fontSize: this._tapFontSize,
      });
    }

    this._layoutLoadingSpine();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CLEANUP
  // ═══════════════════════════════════════════════════════════════════════════

  cleanup() {
    this._stopAutoplay();
    this._destroyLoadingSpine();
    if (this._tapRevealAnims) {
      this._tapRevealAnims.forEach((a) => a.pause());
      this._tapRevealAnims = null;
    }
    if (this._hoverAnim) {
      this._hoverAnim.pause();
      this._hoverAnim = null;
    }
    if (this._pressAnim) {
      this._pressAnim.pause();
      this._pressAnim = null;
    }
    if (this._introAnims) {
      this._introAnims.forEach((a) => a.pause());
      this._introAnims = null;
    }
    this._cancelTrackAnim();

    if (this._slidesMaskContainer) {
      this._slidesMaskContainer.removeAllListeners();
    }
    for (const dot of this._dotEntries) {
      dot.gfx.removeAllListeners();
    }

    if (this._blackBg) {
      this._blackBg.destroy();
      this._blackBg = null;
    }
    if (this._background) {
      this._background.destroy();
      this._background = null;
    }
    if (this._logoGlow) {
      this._logoGlow.destroy();
      this._logoGlow = null;
    }
    if (this._logo) {
      this._logo.destroy();
      this._logo = null;
    }
    if (this._maxWinText) {
      this._maxWinText.destroy();
      this._maxWinText = null;
    }

    if (this._sliderGroup) {
      this._sliderGroup.destroy({ children: true });
      this._sliderGroup = null;
    }
    this._slidesMaskContainer = null;
    this._slidesMaskGfx = null;
    this._slidesTrack = null;
    this._realSlides = [];
    this._dotsContainer = null;
    this._dotEntries = [];

    if (this._tapContainer) {
      if (this._tapText) {
        this._tapText.removeAllListeners();
      }
      this._tapContainer.destroy({ children: true });
      this._tapContainer = null;
      this._tapText = null;
    }

    this._clickHandler = null;
    this._fullscreenHandler = null;
    this.initialized = false;
  }
}

export default IntroPopupComposedController;
